import { DarwinModal } from 'components/darwin-ui/Modal';
import DarwinTypography from 'components/darwin-ui/Typography';
import { useTranslation } from 'react-i18next';
import styles from './index.module.css';
import { DarwinButton, DarwinFlex } from 'components/darwin-ui';
import DarwinGoogleMap from 'components/google-map/googleMap';
import { useState, useEffect } from 'react';
import Icon from 'components/darwin-ui/Icon';
import { LatLng, PolygonItemPayload, PolygonItemUI } from 'types/maps';
import {
  DEFAULT_POLY_OPTIONS,
  rectangleAround,
  updatePolygonsFromMap,
} from 'utils/maps';
import { useUI } from 'contexts/UiContext';
import {
  useCreatePolygon,
  useGetPolygonsByAddress,
} from 'api/services/mapsPolygonService';
import { v4 as uuidv4 } from 'uuid';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { ErrorType } from 'types/genericTypes';
import { Location } from 'types/organizationTypes';
import { ImportButton } from './ImportButton';
import { ExportButton } from './ExportButton';

const { Text } = DarwinTypography;

type MapType = 'roadmap' | 'satellite';

interface GeolocationModalProps {
  geolocationModalOpen: boolean;
  handleCloseModal: () => void;
  addressId: string;
  locationId: string;
  targetCompanyId: string;
  location?: Location;
  center: LatLng;
}

export const GeolocationModal = ({
  geolocationModalOpen,
  handleCloseModal,
  addressId,
  locationId,
  targetCompanyId,
  location,
  center,
}: GeolocationModalProps) => {
  const { t } = useTranslation();
  const [satelliteType, setSatelliteType] = useState<MapType>('roadmap');
  const { selectedCompany } = useUI();
  const { openNotification } = useFloatNotification();
  const { data: polygonsData } = useGetPolygonsByAddress({
    companyId: selectedCompany || '',
    targetCompanyId,
    addressId,
    locationId,
  });

  const { mutateAsync: createPolygon } = useCreatePolygon();

  const [polygons, setPolygons] = useState<PolygonItemUI[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  const [centerMap, setCenterMap] = useState<LatLng>(center);

  // Initialize polygons from API data
  useEffect(() => {
    if (polygonsData?.data?.polygons) {
      const loadedPolygons = polygonsData.data.polygons.map(item => ({
        id: uuidv4(),
        paths: item.paths,
        options: DEFAULT_POLY_OPTIONS,
      }));
      setPolygons(loadedPolygons);
    }
  }, [polygonsData]);

  const handleSatelliteType = (type: MapType) => {
    setSatelliteType(type);
  };

  const handleAddPolygon = () => {
    const triangleCoords = rectangleAround(centerMap, 2000, 1000);
    setPolygons(prev => [
      ...prev,
      {
        id: uuidv4(),
        paths: triangleCoords,
        options: DEFAULT_POLY_OPTIONS,
      },
    ]);
  };

  const handleImport = (importedPolygons: PolygonItemUI[]) => {
    // Replace existing polygons with imported ones
    setPolygons(importedPolygons);
    setActiveId(null);
  };

  const handleSavePolygons = async (saveAndExit: boolean) => {
    const payload = {
      polygons: polygons.map<PolygonItemPayload>(poly => ({
        name: poly.id,
        paths: poly.paths,
      })),
    };

    try {
      await createPolygon({
        payload: payload,
        companyId: selectedCompany || '',
        targetCompanyId,
        addressId,
        locationId,
      });
      openNotification(
        t('success'),
        t('geolocation.modal.polygon-success-creating'),
        'success'
      );
      if (saveAndExit) {
        handleCloseModal();
      }
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('geolocation.modal.polygon-error-creating'),
          'error'
        );
      }
    }
  };

  const handleDeletePolygon = () => {
    if (activeId === null) {
      openNotification(
        t('info'),
        t('geolocation.modal.polygon-non-selected'),
        'info'
      );
      return;
    }
    setPolygons(prev => prev.filter(item => item.id !== activeId));
    setActiveId(null);
    openNotification(
      t('success'),
      t('geolocation.modal.polygon-deleted-success'),
      'success'
    );
  };

  return (
    <DarwinModal
      open={geolocationModalOpen}
      title={
        <Text className={styles.geolocationModalTitle}>
          {t('geolocation.modal.title')}
        </Text>
      }
      footer={
        <>
          <DarwinFlex
            gap={8}
            style={{
              justifyContent: 'end',
            }}
          >
            <DarwinButton onClick={handleCloseModal}>
              {t('geolocation.modal.cancel-title')}
            </DarwinButton>
            <DarwinButton
              type="primary"
              onClick={() => handleSavePolygons(false)}
            >
              {t('geolocation.modal.ok-title')}
            </DarwinButton>
            <DarwinButton
              type="primary"
              onClick={() => handleSavePolygons(true)}
            >
              {t('geolocation.modal.save-and-exit-title')}
            </DarwinButton>
          </DarwinFlex>
        </>
      }
      width={800}
    >
      <DarwinFlex vertical>
        <Text className={styles.subText1}>
          {t('geolocation.modal.subtext-1')}
        </Text>
        <Text className={styles.subText2}>
          {t('geolocation.modal.subtext-2')}
        </Text>
        <DarwinFlex
          style={{
            justifyContent: 'space-between',
            marginBottom: 10,
            marginTop: 10,
          }}
        >
          <DarwinFlex className={styles.optionColor} gap={6}>
            <DarwinButton
              className={`${
                satelliteType === 'roadmap' ? '' : styles.optionNonSelected
              }`}
              onClick={() => handleSatelliteType('roadmap')}
            >
              {t('geolocation.modal.option-map')}
            </DarwinButton>
            <DarwinButton
              className={`${
                satelliteType === 'satellite' ? '' : styles.optionNonSelected
              }`}
              onClick={() => handleSatelliteType('satellite')}
            >
              {t('geolocation.modal.option-satellite')}
            </DarwinButton>
          </DarwinFlex>
          <DarwinFlex className={styles.importExport}>
            <ImportButton
              onImport={handleImport}
              existingCount={polygonsData?.data?.polygons.length || 0}
            />
            <ExportButton
              disabled={
                !polygonsData?.data || polygonsData?.data?.polygons.length === 0
              }
              location={location}
              polygons={polygonsData?.data?.polygons.map(p => p.paths) || []}
            />
          </DarwinFlex>
        </DarwinFlex>
        <DarwinFlex gap={20}>
          <DarwinGoogleMap
            height={400}
            width="100%"
            disableUi
            mapType={satelliteType}
            zoom={10}
            zoomControl
            center={center}
            zoomControlOptions={{
              position: google.maps.ControlPosition.TOP_LEFT,
            }}
            polygons={polygons.map(p => ({
              paths: p.paths,
              options: p.options,
            }))}
            polygonIds={polygons.map(p => p.id)}
            onPolygonsChange={polys => {
              setPolygons(prev => updatePolygonsFromMap(prev, polys));
            }}
            activeId={activeId}
            onPolygonClick={id => setActiveId(id)}
            vertexDeletionMode={'both'}
            fitToPolygonsOnce={true}
            onCenterChange={c => {
              setCenterMap(c);
            }}
            centerChangeEvent="dragend"
          />

          <DarwinFlex vertical gap={15}>
            <DarwinButton
              className={styles.iconOptions}
              onClick={handleAddPolygon}
            >
              <Icon name="square" size="md" />
            </DarwinButton>
            <DarwinButton
              className={styles.iconOptions}
              onClick={handleDeletePolygon}
              disabled={!!!activeId}
            >
              <Icon name="delete" size="xs" />
            </DarwinButton>
          </DarwinFlex>
        </DarwinFlex>
      </DarwinFlex>
    </DarwinModal>
  );
};
